import type {
  AchievementResult,
  AchievementView,
  AttackResult,
  AttackView,
  BaseOpponent,
  BridgePlayer,
  DataDocument,
  DataQuery,
  DataScope,
  EventsAnswer,
  GameUiLook,
  InboxAttack,
  LeaderboardInfo,
  LeaderboardView,
  MatchResult,
  MatchView,
  MyBase,
  OpenMatch,
  RoomTicket,
  SaveInfo,
  ScoreResult,
  TurnUpdate,
} from '@mogzo/shared';
import { DEFAULT_GAME_UI, readGameUiLook, TURN_LIMITS } from '@mogzo/shared';

export interface PlaySession {
  player: BridgePlayer;
  /** Works for this game only, for an hour; kept by the page, never given to the game. */
  token: string;
  expiresAt: string;
}

/** A Game Services call that failed, with the API's code and message for the game. */
export class PlayError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Where a Game Services call goes, and the play token it's made with. */
export interface PlayAuth {
  apiUrl: string;
  token: string;
}

/** How often a busy answer is tried again before the game hears it. */
const BUSY_RETRIES = 3;

/** For writes that may still be on their way when the page closes. */
export interface SendOptions {
  keepalive?: boolean;
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Calls a Game Services route with the game's play token (never the player's session). When
 * MOGZO is busy (M24b), it waits as the API asks, plus a little so players don't all come back
 * at once, and tries again.
 */
async function playFetch<T>(
  auth: PlayAuth,
  path: string,
  init: RequestInit = {},
  timeoutMs = 15_000,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(auth.apiUrl + path, {
      ...init,
      headers: {
        authorization: `Bearer ${auth.token}`,
        ...(init.body ? { 'content-type': 'application/json' } : {}),
      },
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (response.ok) return (await response.json()) as T;
    const body = (await response.json().catch(() => null)) as {
      error?: { code?: string; message?: string };
    } | null;
    const code = body?.error?.code ?? 'unavailable';
    if (response.status === 503 && code === 'busy' && attempt < BUSY_RETRIES) {
      const seconds = Number(response.headers.get('retry-after')) || 2;
      await pause(seconds * 1000 + Math.random() * 1000);
      continue;
    }
    throw new PlayError(
      response.status,
      code,
      body?.error?.message ?? 'MOGZO couldn’t answer just now. Try again.',
    );
  }
}

export async function fetchPlayer(auth: PlayAuth): Promise<BridgePlayer> {
  return (await playFetch<{ player: BridgePlayer }>(auth, '/v1/play/me')).player;
}

const savePath = (key: string) => `/v1/play/saves/${encodeURIComponent(key)}`;

export async function getSave(auth: PlayAuth, key: string): Promise<unknown> {
  const { save } = await playFetch<{ save: { data: unknown } | null }>(auth, savePath(key));
  return save ? save.data : null;
}

export async function setSave(
  auth: PlayAuth,
  key: string,
  value: unknown,
  { keepalive = false }: SendOptions = {},
): Promise<void> {
  const body = JSON.stringify({ data: value });
  await playFetch(auth, savePath(key), {
    method: 'PUT',
    body,
    // Browsers refuse keepalive requests over 64 KB in all.
    keepalive: keepalive && body.length < 60_000,
  });
}

export async function deleteSave(auth: PlayAuth, key: string): Promise<boolean> {
  return (await playFetch<{ deleted: boolean }>(auth, savePath(key), { method: 'DELETE' })).deleted;
}

export async function listSaves(auth: PlayAuth): Promise<SaveInfo[]> {
  return (await playFetch<{ saves: SaveInfo[] }>(auth, '/v1/play/saves')).saves;
}

const documentPath = (collection: string, id: string, scope: DataScope) =>
  `/v1/play/data/${encodeURIComponent(collection)}/${encodeURIComponent(id)}?scope=${scope}`;

export async function getDocument(
  auth: PlayAuth,
  collection: string,
  scope: DataScope,
  id: string,
): Promise<DataDocument | null> {
  return (
    await playFetch<{ document: DataDocument | null }>(auth, documentPath(collection, id, scope))
  ).document;
}

export function setDocument(
  auth: PlayAuth,
  collection: string,
  scope: DataScope,
  id: string,
  value: unknown,
): Promise<DataDocument> {
  return playFetch(auth, documentPath(collection, id, scope), {
    method: 'PUT',
    body: JSON.stringify({ data: value }),
  });
}

export async function deleteDocument(
  auth: PlayAuth,
  collection: string,
  scope: DataScope,
  id: string,
): Promise<boolean> {
  return (
    await playFetch<{ deleted: boolean }>(auth, documentPath(collection, id, scope), {
      method: 'DELETE',
    })
  ).deleted;
}

export async function queryDocuments(
  auth: PlayAuth,
  collection: string,
  scope: DataScope,
  query: DataQuery,
): Promise<DataDocument[]> {
  return (
    await playFetch<{ documents: DataDocument[] }>(
      auth,
      `/v1/play/data/${encodeURIComponent(collection)}/query`,
      { method: 'POST', body: JSON.stringify({ scope, query }) },
    )
  ).documents;
}

/** The config with the live events that are on, and when an event next turns on or off (M37). */
export async function getConfig(
  auth: PlayAuth,
): Promise<{ config: unknown; changesAt?: string | null }> {
  return playFetch<{ config: unknown; changesAt?: string | null }>(auth, '/v1/play/config');
}

/** The live events that are on (M37). */
export function getEvents(auth: PlayAuth): Promise<EventsAnswer> {
  return playFetch<EventsAnswer>(auth, '/v1/play/events');
}

export function submitScore(auth: PlayAuth, board: string, score: number): Promise<ScoreResult> {
  return playFetch(auth, `/v1/play/leaderboards/${encodeURIComponent(board)}/scores`, {
    method: 'POST',
    body: JSON.stringify({ score }),
  });
}

export function listAchievements(auth: PlayAuth): Promise<AchievementView[]> {
  return playFetch<{ achievements: AchievementView[] }>(auth, '/v1/play/achievements').then(
    (body) => body.achievements,
  );
}

export function unlockAchievement(auth: PlayAuth, id: string): Promise<AchievementResult> {
  return playFetch(auth, `/v1/play/achievements/${encodeURIComponent(id)}/unlock`, {
    method: 'POST',
  });
}

export function achievementProgress(
  auth: PlayAuth,
  id: string,
  value: number,
  { keepalive = false }: SendOptions = {},
): Promise<AchievementResult> {
  return playFetch(auth, `/v1/play/achievements/${encodeURIComponent(id)}/progress`, {
    method: 'POST',
    body: JSON.stringify({ value }),
    keepalive,
  });
}

export function getLeaderboard(
  auth: PlayAuth,
  board: string,
  view: 'top' | 'around',
  period: 'current' | 'previous',
  limit: number,
): Promise<LeaderboardView> {
  const query = new URLSearchParams({ view, period, limit: String(limit) });
  return playFetch(auth, `/v1/play/leaderboards/${encodeURIComponent(board)}?${query}`);
}

/** The look of MOGZO's panels over this game (M19); one in an older shape is read as such. */
export async function getUiLook(auth: PlayAuth): Promise<GameUiLook> {
  const { look } = await playFetch<{ look: unknown }>(auth, '/v1/play/ui');
  return readGameUiLook(look) ?? DEFAULT_GAME_UI;
}

// --- Matches (M31) -----------------------------------------------------------------------

const matchPath = (id: string) => `/v1/play/matches/${encodeURIComponent(id)}`;
const post = (body?: unknown): RequestInit => ({
  method: 'POST',
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
});

/**
 * The play API's matches, in the shape MOGZO's multiplayer panel uses. `withToken` makes each
 * call with the play token (a new one when the hour ran out).
 */
export function matchCalls(withToken: <T>(call: (auth: PlayAuth) => Promise<T>) => Promise<T>) {
  const call = <T>(path: string, init?: RequestInit, timeoutMs?: number) =>
    withToken((auth) => playFetch<T>(auth, path, init, timeoutMs));
  return {
    find: (mode: string) => call<MatchView>('/v1/play/matches/find', post({ mode })),
    create: (mode: string) => call<MatchView>('/v1/play/matches', post({ mode })),
    join: (code: string) => call<MatchView>('/v1/play/matches/join', post({ code })),
    poll: (id: string) => call<MatchView>(matchPath(id)),
    start: (id: string) => call<MatchView>(`${matchPath(id)}/start`, post()),
    leave: async (id: string) => {
      await call(`${matchPath(id)}/leave`, post());
    },
    rematch: (id: string) => call<MatchView>(`${matchPath(id)}/rematch`, post()),
    results: (id: string, result: MatchResult) =>
      call<MatchView>(`${matchPath(id)}/results`, post({ result })),
    /** Turns (M32): what happened after event `after`; `wait` holds it open for the next move. */
    turns: (id: string, after: number, wait = false) =>
      call<TurnUpdate>(
        `${matchPath(id)}/turns?after=${after}&wait=${wait ? 1 : 0}`,
        undefined,
        // Held open up to TURN_LIMITS.waitSeconds, plus time for the answer to arrive.
        wait ? (TURN_LIMITS.waitSeconds + 10) * 1000 : undefined,
      ),
    move: (
      id: string,
      body: { move: unknown; state: unknown; expect?: number; keepTurn?: boolean },
    ) => call<TurnUpdate>(`${matchPath(id)}/moves`, post(body)),
    mine: () => call<{ matches: OpenMatch[] }>('/v1/play/matches').then((body) => body.matches),
    /** Realtime (M35): a ticket into the match's room. */
    room: (id: string) => call<RoomTicket>(`${matchPath(id)}/room`, post()),
  };
}
export type MatchCalls = ReturnType<typeof matchCalls>;

// --- Bases (M34) ---------------------------------------------------------------------------

const basePath = (mode: string) => `/v1/play/bases/${encodeURIComponent(mode)}`;

/** The play API's bases: the player's own, one to attack, results and the attack log. */
export function baseCalls(withToken: <T>(call: (auth: PlayAuth) => Promise<T>) => Promise<T>) {
  const call = <T>(path: string, init?: RequestInit, timeoutMs?: number) =>
    withToken((auth) => playFetch<T>(auth, path, init, timeoutMs));
  return {
    save: (mode: string, base: unknown) =>
      call<MyBase>(basePath(mode), { method: 'PUT', body: JSON.stringify({ base }) }),
    mine: (mode: string) => call<{ base: MyBase | null }>(basePath(mode)).then((body) => body.base),
    findOpponent: (mode: string) =>
      call<{ opponent: BaseOpponent | null }>(`${basePath(mode)}/opponent`, post()).then(
        (body) => body.opponent,
      ),
    report: (attack: string, result: AttackResult) =>
      call<AttackView>(`/v1/play/attacks/${encodeURIComponent(attack)}`, post({ result })),
    inbox: (mode: string) =>
      call<{ attacks: InboxAttack[] }>(`${basePath(mode)}/inbox`).then((body) => body.attacks),
  };
}

/** The game's leaderboards, for the leaderboard panel to switch between. */
export async function listBoards(auth: PlayAuth): Promise<Omit<LeaderboardInfo, 'period'>[]> {
  return (
    await playFetch<{ leaderboards: Omit<LeaderboardInfo, 'period'>[] }>(
      auth,
      '/v1/play/leaderboards',
    )
  ).leaderboards;
}
